import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { useCategories } from '../hooks/useCategories';
import type { Category } from '../types';

const PAGE_SIZE = 10;

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CategoriesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const {
    categories,
    total,
    totalPages: apiTotalPages,
    loading,
    error,
    refresh,
    deleteCategory,
  } = useCategories({
    search: search.trim() || undefined,
    page: currentPage,
    pageSize: PAGE_SIZE,
  });

  const totalPages = Math.max(1, apiTotalPages);
  const pageToShow = Math.min(currentPage, totalPages) || 1;

  const handleDelete = async (id: string, nome: string) => {
    if (!confirm(`Excluir a categoria "${nome}"? Os contatos desta categoria ficarão sem categoria.`)) return;
    await deleteCategory(id);
  };

  if (error) {
    return (
      <>
        <Header title="Categorias" subtitle="Erro" compactTopbar />
        <PageLayout>
          <div className="card-padrao p-8 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => refresh()}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Tentar novamente
            </button>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Categorias" subtitle={`${total} categoria(s)`} compactTopbar />
      <PageLayout>
        <PageHeader
          title="Categorias"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos', href: '/contatos' },
            { label: 'Categorias' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Categorias</h2>
              {(total > 0 || search.trim() !== '') && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative w-48 sm:w-56">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" aria-hidden />
                    <input
                      type="text"
                      placeholder="Buscar por nome..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/contatos/categorias/novo')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  >
                    <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                    Adicionar categoria
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando...</span>
              </div>
            ) : total === 0 && search.trim() === '' ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4 text-gray-300 dark:text-gray-600" aria-hidden>
                  <i className="fas fa-tags" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Nenhuma categoria
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Crie categorias para organizar seus contatos (ex: Vip, Suporte, Leads).
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/contatos/categorias/novo')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                >
                  <i className="fas fa-plus text-base" aria-hidden />
                  Adicionar categoria
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contatos')}
                  className="ml-3 inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Voltar para Contatos
                </button>
              </div>
            ) : total === 0 && search.trim() !== '' ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                Nenhuma categoria encontrada para &quot;{search}&quot;. Altere o termo de busca ou limpe o campo.
              </div>
            ) : categories.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                Nenhuma categoria encontrada para &quot;{search}&quot;.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">Cor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descrição</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contatos</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Atualizado em</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {categories.map((cat: Category & { _count?: { contacts: number } }) => (
                        <tr key={cat.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600"
                              style={{ backgroundColor: cat.cor }}
                              aria-label={`Cor: ${cat.cor}`}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{cat.nome}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{cat.descricao || '–'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200">
                              {cat._count?.contacts ?? 0} contatos
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDate(cat.atualizadoEm)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2 justify-end">
                              <button
                                type="button"
                                onClick={() => navigate(`/contatos/categorias/${cat.id}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 font-medium transition-colors"
                                title="Editar categoria"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(cat.id, cat.nome)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-medium transition-colors"
                                title="Excluir categoria"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Mostrando {(pageToShow - 1) * PAGE_SIZE + 1}–{Math.min(pageToShow * PAGE_SIZE, total)} de {total} categoria(s)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={pageToShow <= 1}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Página {pageToShow} de {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={pageToShow >= totalPages}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {total > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/contatos')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
              >
                <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                Voltar para Contatos
              </button>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
}
